import { resolveMediaUrl } from '../../../utils/helpers';
import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  Calendar, LayoutGrid, DollarSign, Users, MapPin,
  Edit2, Trash2, Plus, CheckCircle, XCircle, AlertTriangle,
  Settings, Globe, ChevronDown, Loader
} from 'lucide-react';
import toast from 'react-hot-toast';
import CreateCourtModal from '../../components/CreateCourtModal';
import AttachPriceModal from '../../../payments/components/PaymentPriceModal';
import CalendarAndEvent from '../../../events/components/CalendarAndEvent';
import { academyAPI } from '../../../services/api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/**
 * public_hours is gone.
 *
 * A weekday x hour grid with no dates lived here, in two competing JSON shapes
 * written by two different editors on this same screen — whichever saved last
 * winning — and it could not express "closed on the 25th" at all. A court's
 * hours are now dated events on its own schedule, read the same way as every
 * other diary in the app.
 */

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------
const STATUS_CONFIG = {
  available: {
    label: 'Available',
    badgeClass: 'bg-green-100 text-green-700',
    dotClass: 'bg-green-500',
    cardAccent: 'border-l-4 border-green-500',
    Icon: CheckCircle,
    iconClass: 'text-green-500'
  },
  occupied: {
    label: 'Occupied',
    badgeClass: 'bg-red-100 text-red-700',
    dotClass: 'bg-red-500',
    cardAccent: 'border-l-4 border-red-500',
    Icon: XCircle,
    iconClass: 'text-red-500'
  },
  reserved: {
    label: 'Reserved',
    badgeClass: 'bg-orange-100 text-orange-700',
    dotClass: 'bg-orange-500',
    cardAccent: 'border-l-4 border-orange-500',
    Icon: Calendar,
    iconClass: 'text-orange-500'
  },
  maintenance: {
    label: 'Maintenance',
    badgeClass: 'bg-gray-200 text-gray-600',
    dotClass: 'bg-gray-400',
    cardAccent: 'border-l-4 border-gray-400',
    Icon: Settings,
    iconClass: 'text-gray-500'
  }
};

/**
 * One switch: may the public book this court?
 *
 * This was a collapsible panel holding a toggle, a price and a weekly hours
 * grid. The hours moved to the court's schedule, where they can carry dates.
 * The price moved to the open windows, where it can differ by evening, weekend
 * and season — leaving a rate here that only applies when a window sets none,
 * which is edited with the rest of the court's details rather than hidden
 * behind a disclosure triangle.
 *
 * What is left genuinely belongs to the court and nowhere else: whether it is
 * listed for public booking at all. The availability endpoint returns nothing
 * for a court with this off, however many open windows it has.
 */
const PublicBookingToggle = ({ court, onUpdate, theme }) => (
  <div className={`flex items-center justify-between gap-2 border-t ${theme.border} pt-3 mt-1`}>
    <div className="min-w-0">
      <span className={`text-xs font-medium ${theme.text} flex items-center gap-1.5`}>
        <Globe size={13} /> Public booking
      </span>
      <p className={`text-[11px] ${theme.textMuted} mt-0.5`}>
        {court.publicBookingEnabled
          ? 'Anyone can book the open hours on this court'
          : 'Hidden from players — only staff can book it'}
      </p>
    </div>
    <button
      onClick={() => onUpdate(court.id, { publicBookingEnabled: !court.publicBookingEnabled })}
      className={`relative inline-flex h-5 w-9 rounded-full transition-colors flex-shrink-0 ${
        court.publicBookingEnabled ? theme.tabActive : 'bg-gray-300'
      }`}
      aria-label="Toggle public booking"
    >
      <span className={`inline-block w-3.5 h-3.5 bg-white rounded-full shadow transform transition-transform mt-0.5 ${
        court.publicBookingEnabled ? 'translate-x-4' : 'translate-x-0.5'
      }`} />
    </button>
  </div>
);

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

// ---- Status Badge ----
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.available;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.badgeClass}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotClass}`} />
      {cfg.label}
    </span>
  );
};

// ---- Status Select (admin-editable status control, Gap C) ----
const StatusSelect = ({ court, onUpdateStatus }) => {
  const cfg = STATUS_CONFIG[court.status] || STATUS_CONFIG.available;
  return (
    <select
      value={court.status}
      onChange={e => onUpdateStatus(court.id, e.target.value)}
      onClick={e => e.stopPropagation()}
      className={`inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-semibold border-0 cursor-pointer ${cfg.badgeClass}`}
    >
      {Object.entries(STATUS_CONFIG).map(([value, c]) => (
        <option key={value} value={value}>{c.label}</option>
      ))}
    </select>
  );
};

// ---- Court Card (List View) ----
const CourtCard = ({ court, isAdmin, onEdit, onPrice, onDelete, onUpdatePublic, onUpdateStatus, confirmDeleteId, setConfirmDeleteId, onOpenSchedule }) => {
  const { theme } = useTheme();
  const cfg = STATUS_CONFIG[court.status] || STATUS_CONFIG.available;

  return (
    <div className={`${theme.card} rounded-xl p-5 flex flex-col gap-3 ${cfg.cardAccent}`}>
      {/* Header row */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {/* The name opens this court's schedule. A court is a thing with a
                diary, and the list is how you get to it — a separate tab that
                then asks you to pick the court again is one step too many. */}
            <button
              type="button"
              onClick={() => onOpenSchedule?.(court.id)}
              className={`font-semibold text-base ${theme.text} truncate text-left ${theme.linkHover} hover:underline underline-offset-2 transition-colors`}
              title={`Open ${court.name}'s schedule`}
            >
              {court.name}
            </button>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              court.type === 'court'
                ? `bg-blue-100 ${theme.link}`
                : 'bg-purple-100 text-purple-700'
            }`}>
              {court.type === 'court' ? 'Court' : 'Field'}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {isAdmin ? <StatusSelect court={court} onUpdateStatus={onUpdateStatus} /> : <StatusBadge status={court.status} />}
          {/* What the diary says, next to what somebody declared. `status` is a
              field an admin picks and nothing keeps it true — a court could read
              "Available" with a session being played on it. This line comes from
              the bookings. */}
          {court.busyNow ? (
            <span className="text-[11px] font-medium text-red-600 whitespace-nowrap">
              In use until {new Date(court.busyNow.until).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
            </span>
          ) : (
            <span className={`text-[11px] ${theme.textMuted} whitespace-nowrap`}>Free right now</span>
          )}
        </div>
      </div>

      {/* Sports, said as such. Bare chips beside a court's name read as part of
          the name — "Test Court Pickleball Tennis". */}
      <div className="flex items-center flex-wrap gap-1.5">
        <span className={`text-xs font-medium ${theme.textSecondary}`}>Sports</span>
        {court.sports?.length ? court.sports.map(s => (
          <span key={s} className={`px-2 py-0.5 ${theme.tag} rounded text-xs`}>{s}</span>
        )) : <span className={`text-xs ${theme.textMuted} italic`}>none set</span>}
      </div>

      {/* Court images */}
      {court.imageKeys?.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {court.imageKeys.slice(0, 3).map((key, i) => (
            <img
              key={key}
              src={resolveMediaUrl(`/images/${key}`)}
              alt={`${court.name} image ${i + 1}`}
              className="h-20 w-28 object-cover rounded-lg flex-shrink-0"
            />
          ))}
          {court.imageKeys.length > 3 && (
            <div className="h-20 w-28 rounded-lg flex-shrink-0 bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs font-medium text-gray-500">
              +{court.imageKeys.length - 3} more
            </div>
          )}
        </div>
      )}

      {/* Description */}
      {court.description && (
        <p className={`text-sm ${theme.textSecondary} line-clamp-2`}>{court.description}</p>
      )}

      {/* Stats row */}
      <div className={`flex items-center gap-4 text-sm ${theme.textMuted}`}>
        <span className="flex items-center gap-1">
          <Users className="w-3.5 h-3.5" />
          {court.capacity != null ? `${court.capacity} players` : <span className="italic">Capacity N/A</span>}
        </span>
        {/* ⚠️ THE TAG, NOT `hourlyRate`.
            
            This showed "$200/hr default" from the legacy column, which NO
            payment path reads any more — `PaymentSellablesService.court()`
            prices an hour from the tag and returns nothing without one. So a
            court could advertise a rate here and refuse to be booked, with the
            card insisting it was priced.
            
            Still "default", because a window on the Schedule tab can price its
            own evening; this is what applies when none does. */}
        <span className="flex items-center gap-1"
          title="What an hour costs when an open window names no price of its own">
          <DollarSign className="w-3.5 h-3.5" />
          {court.price
            ? (
              <span>
                {/* Named, so a test compares the rate rather than the sentence
                    around it — the tag's own label can contain digits. */}
                <span data-testid="court-card-rate">{`$${court.price.amount}`}</span>
                {`/hr default${court.price.label ? ` · ${court.price.label}` : ''}`}
              </span>
            )
            : <span className="italic">Not priced — cannot be booked</span>}
        </span>
      </div>

      {/* Admin actions */}
      {/* Public booking settings */}
      {isAdmin && (
        <PublicBookingToggle court={court} onUpdate={onUpdatePublic} theme={theme} />
      )}

      {/* Edit / Delete actions */}
      {isAdmin && (
        <div className="flex items-center gap-2 pt-1 border-t mt-auto" style={{ borderColor: 'inherit' }}>
          {confirmDeleteId === court.id ? (
            <div className="flex items-center gap-2 w-full">
              <span className={`text-xs ${theme.textSecondary} flex-1`}>Delete this court?</span>
              <button
                onClick={() => onDelete(court.id)}
                className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 transition-colors"
              >
                Yes, delete
              </button>
              <button
                onClick={() => setConfirmDeleteId(null)}
                className={`px-3 py-1.5 border ${theme.border} rounded-lg text-xs ${theme.text} ${theme.cardHover} transition-colors`}
              >
                Cancel
              </button>
            </div>
          ) : (
            <>
              {/* The way into this court's diary. It was only the court's name,
                  which nothing marked as a link — a door with no handle. */}
              <button
                onClick={() => onOpenSchedule?.(court.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 ${theme.buttonPrimary} rounded-lg text-xs font-medium transition-colors`}
              >
                <Calendar className="w-3.5 h-3.5" />
                Schedule
              </button>
              <button
                onClick={() => onEdit(court)}
                className={`flex items-center gap-1.5 px-3 py-1.5 ${theme.buttonSecondary || `border ${theme.border} ${theme.text} ${theme.cardHover}`} rounded-lg text-xs font-medium transition-colors`}
              >
                <Edit2 className="w-3.5 h-3.5" />
                Edit
              </button>
              {/* ITS OWN ACTION RATHER THAN A FIELD IN THE EDIT FORM.
                  
                  A price is a row with its own life — withdrawn and replaced,
                  dated for a season, never deleted, because order lines point at
                  the tag they were bought at. That does not fit a text input
                  saved alongside the court's name, and a court being created
                  has no id for a tag to hang off yet. Same modal every other
                  priced offering uses. */}
              <button
                onClick={() => onPrice?.(court)}
                className={`flex items-center gap-1.5 px-3 py-1.5 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg text-xs font-medium transition-colors`}
              >
                <DollarSign className="w-3.5 h-3.5" />
                Pricing
              </button>
              <button
                onClick={() => setConfirmDeleteId(court.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 border ${theme.border} ${theme.textSecondary} rounded-lg text-xs font-medium hover:border-red-400 hover:text-red-600 transition-colors`}
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Pick a court, then edit that court's own schedule.
 *
 * Replaces the weekday×hour grid that used to live here. That grid wrote
 * `public_hours`, which had no dates in it — so "closed on the 25th" could only
 * be said by editing the weekly pattern and remembering to put it back — and it
 * was one of TWO editors on this screen writing the same column in two different
 * JSON shapes, whichever saved last winning. Both are gone.
 */
const CourtScheduleView = ({ courts, academyId, selectedCourtId, onSelectCourt }) => {
  const { theme } = useTheme();
  // Which court is showing is owned by the tab, not by this view: clicking a
  // court in the list has to be able to open that court's schedule, and it
  // cannot reach in here to say which one.
  const court = courts.find(c => c.id === selectedCourtId) || courts[0];
  if (!court) return null;

  return (
    <div className="space-y-4">
      {courts.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {courts.map(c => (
            <button
              key={c.id}
              onClick={() => onSelectCourt(c.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${
                c.id === court.id
                  ? `${theme.tabActive} ${theme.borderAccent}`
                  : `${theme.border} ${theme.text} ${theme.cardHover}`
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}
      <div className={`${theme.card} rounded-xl p-4`}>
        <div className="flex items-baseline justify-between mb-3">
          <p className={`text-sm font-semibold ${theme.text}`}>{court.name}</p>
          <p className={`text-xs ${theme.textMuted}`}>
            {court.hourlyRate ? `$${court.hourlyRate}/hour` : 'No hourly rate set — this court is free to hire'}
          </p>
        </div>
        {/* The same event manager Programs → Sessions and Squads → Schedule
            use — Today/Week/Month/List, Add Event, and price attachment. A
            court is just another owner of events, so it gets the screen that
            already exists rather than one built beside it. */}
        <CalendarAndEvent
          ownerType="court"
          ownerId={court.id}
          academyId={academyId}
          isAdmin
          isMember
          allowResponses={false}
        />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const CourtsTab = ({ academyId, userRole }) => {
  const { theme } = useTheme();

  const isAdmin = userRole === 'admin' || userRole === 'owner' || userRole === 'coach';

  // Shared state
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('list'); // 'list' | 'availability'
  // The court whose schedule is open. Set by clicking a court in the list, so
  // "which court" survives the jump between the two views.
  const [selectedCourtId, setSelectedCourtId] = useState(null);
  const openSchedule = (courtId) => { setSelectedCourtId(courtId); setActiveView('availability'); };

  // List view
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCourt, setEditingCourt] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [pricingCourt, setPricingCourt] = useState(null);

  // Availability

  // Load courts from API
  useEffect(() => {
    if (!academyId) return;
    setLoading(true);
    academyAPI.courts.list(academyId)
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setCourts(list);
      })
      .catch(() => toast.error('Failed to load courts'))
      .finally(() => setLoading(false));
  }, [academyId]);

  // Save from CreateCourtModal (create or edit)
  const handleSaveCourt = useCallback(async (courtData) => {
    try {
      const saved = editingCourt
        ? await academyAPI.courts.update(academyId, editingCourt.id, courtData)
        : await academyAPI.courts.create(academyId, courtData);

      /* RE-READ RATHER THAN PATCH IN THE RESPONSE.
       *
       * The price is not part of this payload — the pricing panel inside the
       * form writes it straight to `prices`, the same way it does for a
       * session. So the court's row may now point at a different tag than the
       * one this response was built from, and patching it in would put the old
       * rate back on the card. */
      const fresh = await academyAPI.courts.list(academyId).catch(() => null);
      if (Array.isArray(fresh)) setCourts(fresh);
      else setCourts(prev => (editingCourt
        ? prev.map(c => (c.id === editingCourt.id ? saved : c))
        : [...prev, saved]));

      toast.success(`"${saved.name}" ${editingCourt ? 'updated' : 'added'}`);
      setShowCreateModal(false);
      setEditingCourt(null);
    } catch (err) {
      toast.error(err?.message || 'Failed to save court');
    }
  }, [academyId, editingCourt]);

  const handleOpenEdit = (court) => {
    setEditingCourt(court);
    setShowCreateModal(true);
  };

  const handleDeleteCourt = async (courtId) => {
    try {
      await academyAPI.courts.remove(academyId, courtId);
      setCourts(prev => prev.filter(c => c.id !== courtId));
      setConfirmDeleteId(null);
      toast.success('Court removed');
    } catch {
      toast.error('Failed to delete court');
    }
  };

  const handleUpdatePublic = async (courtId, updates) => {
    try {
      const updated = await academyAPI.courts.update(academyId, courtId, updates);
      setCourts(prev => prev.map(c => c.id === courtId ? updated : c));
    } catch {
      toast.error('Failed to save settings');
    }
  };

  const handleUpdateStatus = async (courtId, status) => {
    const previous = courts;
    setCourts(prev => prev.map(c => c.id === courtId ? { ...c, status } : c));
    try {
      const updated = await academyAPI.courts.update(academyId, courtId, { status });
      setCourts(prev => prev.map(c => c.id === courtId ? updated : c));
    } catch {
      setCourts(previous);
      toast.error('Failed to update status');
    }
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
    setEditingCourt(null);
  };

  // ---- Render ----
  const VIEW_TABS = [
    { id: 'list', label: 'Court List', Icon: LayoutGrid },
    // "Schedule", not "Availability": the tab now holds the hours a court is
    // open, the hours it is closed and everything booked on it, which is more
    // than availability and is the same word the rest of the app uses.
    { id: 'availability', label: 'Schedule', Icon: Calendar },
  ];

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader className={`w-8 h-8 animate-spin ${theme.link}`} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* View toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className={`flex items-center gap-1 p-1 ${theme.card} rounded-xl`}>
          {VIEW_TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setActiveView(id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeView === id
                  ? theme.tabActive
                  : `${theme.text} ${theme.cardHover}`
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Add Court button (list view, admin) */}
        {activeView === 'list' && isAdmin && (
          <button
            onClick={() => { setEditingCourt(null); setShowCreateModal(true); }}
            className={`flex items-center gap-2 px-4 py-2 ${theme.buttonPrimary} rounded-lg text-sm font-medium  transition-colors`}
          >
            <Plus className="w-4 h-4" />
            Add Court/Field
          </button>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* LIST VIEW                                                           */}
      {/* ------------------------------------------------------------------ */}
      {activeView === 'list' && (
        <>
          {courts.length === 0 ? (
            <div className={`${theme.card} rounded-xl text-center py-16`}>
              <MapPin className={`w-12 h-12 mx-auto mb-3 ${theme.textMuted}`} />
              <p className={`font-semibold ${theme.text} mb-1`}>No courts or fields yet</p>
              <p className={`text-sm ${theme.textMuted}`}>
                {isAdmin ? 'Click "Add Court/Field" to get started.' : 'Check back later.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {courts.map(court => (
                <CourtCard
                  key={court.id}
                  court={court}
                  isAdmin={isAdmin}
                  onEdit={handleOpenEdit}
                  onPrice={setPricingCourt}
                  onOpenSchedule={openSchedule}
                  onDelete={handleDeleteCourt}
                  onUpdatePublic={handleUpdatePublic}
                  onUpdateStatus={handleUpdateStatus}
                  confirmDeleteId={confirmDeleteId}
                  setConfirmDeleteId={setConfirmDeleteId}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* AVAILABILITY VIEW                                                   */}
      {/* ------------------------------------------------------------------ */}
      {activeView === 'availability' && (
        <>
          {courts.length === 0 ? (
            <div className={`${theme.card} rounded-xl text-center py-16`}>
              <Calendar className={`w-12 h-12 mx-auto mb-3 ${theme.textMuted}`} />
              <p className={`font-semibold ${theme.text}`}>No courts to configure</p>
            </div>
          ) : (
            <CourtScheduleView
              courts={courts}
              academyId={academyId}
              selectedCourtId={selectedCourtId}
              onSelectCourt={setSelectedCourtId}
            />
          )}
        </>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* CREATE / EDIT MODAL                                                 */}
      {/* ------------------------------------------------------------------ */}
      {showCreateModal && (
        <CreateCourtModal
          isOpen={showCreateModal}
          onClose={handleModalClose}
          onSave={handleSaveCourt}
          editingCourt={editingCourt}
          academyId={academyId}
        />
      )}

      {/* ------------------------------------------------------------------ */}
      {/* PRICING MODAL                                                       */}
      {/* ------------------------------------------------------------------ */}
      {/* RE-READ ON CLOSE, rather than trusting the card to still be right.
          The modal can create a tag, withdraw one, or replace it, and the court
          row's `price_id` moves with it — so the card's copy is stale the
          moment the modal opens. Showing a withdrawn price as the rate is how a
          court advertises something checkout refuses. */}
      {pricingCourt && (
        <AttachPriceModal
          isOpen
          onClose={async () => {
            setPricingCourt(null);
            const fresh = await academyAPI.courts.list(academyId).catch(() => null);
            if (Array.isArray(fresh)) setCourts(fresh);
          }}
          academyId={academyId}
          ownerType="court"
          ownerId={pricingCourt.id}
          entityName={pricingCourt.name}
        />
      )}
    </div>
  );
};

export default CourtsTab;
